import React, { useState, useEffect } from 'react';
import { dashboardApi } from '../api';
import { StatCard, Card, PageTitle, Badge, Spinner } from '../components/common/UI';
import { MessageSquare, CheckCircle, XCircle, Clock, Activity } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import toast from 'react-hot-toast';

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [msgStats, setMsgStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [s, m] = await Promise.all([
        dashboardApi.summary(),
        dashboardApi.messageStats({ period: '7d' }),
      ]);
      setSummary(s.data.data);
      setMsgStats(m.data.data);
    } catch (_e) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
        <Spinner size={32} />
      </div>
    );
  }

  const wa = summary ? summary.whatsapp : {};
  const msg = summary ? summary.messages : {};
  const q = summary ? summary.queue : {};

  const chartData = (msgStats && msgStats.timeSeries ? msgStats.timeSeries : []).map((d) => ({
    date: d._id ? d._id.slice(5) : '',
    sent: d.sent || 0,
    delivered: d.delivered || 0,
    failed: d.failed || 0,
  }));

  return (
    <div>
      <PageTitle sub={summary ? ('Last updated: ' + new Date(summary.timestamp).toLocaleTimeString()) : ''}>
        Dashboard
      </PageTitle>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard label="Total Messages" value={msg.total ? msg.total.toLocaleString() : '0'} sub={'Today: ' + (msg.today || 0)} icon={MessageSquare} />
        <StatCard label="This Week" value={msg.thisWeek ? msg.thisWeek.toLocaleString() : '0'} icon={CheckCircle} color="#25d366" />
        <StatCard label="Failed" value={msg.failed || 0} sub={(msg.successRate ? msg.successRate.toFixed(1) : '0') + '% success'} icon={XCircle} color="#ef4444" />
        <StatCard label="Pending" value={msg.pending || 0} sub={'Queue: ' + (q.waiting || 0) + ' waiting'} icon={Clock} color="#f59e0b" />
        <StatCard label="Queue Active" value={q.active || 0} sub={'Completed: ' + (q.completed ? q.completed.toLocaleString() : '0')} icon={Activity} color="#3b82f6" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 16, marginBottom: 24 }}>
        <Card>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9', marginBottom: 16 }}>Message Activity (7 days)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="date" stroke="#64748b" tick={{ fontSize: 11 }} />
              <YAxis stroke="#64748b" tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 6 }} />
              <Line type="monotone" dataKey="sent" stroke="#25d366" dot={false} strokeWidth={2} />
              <Line type="monotone" dataKey="delivered" stroke="#3b82f6" dot={false} strokeWidth={2} />
              <Line type="monotone" dataKey="failed" stroke="#ef4444" dot={false} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
          <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
            {[['Sent', '#25d366'], ['Delivered', '#3b82f6'], ['Failed', '#ef4444']].map(function(item) {
              return (
                <div key={item[0]} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#94a3b8' }}>
                  <div style={{ width: 12, height: 3, background: item[1], borderRadius: 2 }} />
                  {item[0]}
                </div>
              );
            })}
          </div>
        </Card>

        <Card>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9', marginBottom: 16 }}>WhatsApp Status</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#64748b', fontSize: 13 }}>Status</span>
              <Badge color={wa.status === 'connected' ? 'green' : 'red'}>{wa.status || 'unknown'}</Badge>
            </div>
            {wa.deviceInfo && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#64748b', fontSize: 13 }}>Name</span>
                  <span style={{ color: '#e2e8f0', fontSize: 13 }}>{wa.deviceInfo.pushName}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#64748b', fontSize: 13 }}>Platform</span>
                  <span style={{ color: '#e2e8f0', fontSize: 13 }}>{wa.deviceInfo.platform}</span>
                </div>
                {wa.deviceInfo.battery && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b', fontSize: 13 }}>Battery</span>
                    <span style={{ color: '#e2e8f0', fontSize: 13 }}>{wa.deviceInfo.battery}%</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#64748b', fontSize: 13 }}>Phone</span>
                  <span style={{ color: '#e2e8f0', fontSize: 11 }}>{wa.deviceInfo.phone}</span>
                </div>
              </>
            )}
            {wa.lastConnected && (
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 4 }}>
                <span style={{ color: '#64748b', fontSize: 13 }}>Last connected</span>
                <span style={{ color: '#94a3b8', fontSize: 11 }}>{new Date(wa.lastConnected).toLocaleString()}</span>
              </div>
            )}
          </div>
        </Card>
      </div>

      {msgStats && msgStats.topRecipients && msgStats.topRecipients.length > 0 && (
        <Card>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9', marginBottom: 16 }}>Top Recipients</h3>
          {msgStats.topRecipients.slice(0, 5).map((r, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #334155' }}>
              <span style={{ color: '#e2e8f0', fontSize: 13 }}>+{r._id}</span>
              <span style={{ color: '#25d366', fontWeight: 600, fontSize: 13 }}>{r.count} messages</span>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
